import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { RazorpayCheckout } from './RazorpayCheckout';

interface PricingTier {
  id: string;
  name: string;
  description: string;
  popular: boolean;
  pricing: {
    monthly: number;
    annual: number;
  };
  limits: {
    scans: {
      included: number;
      overageAllowed: boolean;
      overageRate: number;
    };
    teamSize: {
      recommended: string;
      note: string;
    };
    securityRules: {
      count: number | string;
      type: string;
    };
    customRules: {
      perMonth: number;
    };
    plugins: {
      count: number | string;
      note?: string;
    };
  };
  features: {
    outputFormats: string[];
    advancedAnalytics: boolean;
    dashboardAccess: boolean;
    detailedRemediation: boolean;
    teamManagement: boolean;
    apiAccess: boolean;
    whiteLabel?: boolean;
  };
  support: {
    type: string;
    responseTime: string;
  };
  restrictions?: {
    commercialUse: boolean;
    description: string;
  };
}

export function PricingPlans() {
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);

  // Get pre-selected plan from URL params
  const preSelectedPlan = searchParams.get('plan');
  const paymentSuccess = searchParams.get('payment') === 'success';

  useEffect(() => {
    loadPricingTiers();
  }, []);

  const loadPricingTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch pricing data directly from pricing.json
      const [pricingRes, billingRes] = await Promise.all([
        fetch('/pricing.json'),
        apiClient.getMyBilling().catch(() => null),
      ]);
      if (!pricingRes.ok) {
        throw new Error('Failed to load pricing data');
      }

      const pricingData = await pricingRes.json();
      setTiers(pricingData.tiers || []);

      const planId = billingRes?.plan?.id ? String(billingRes.plan.id).toLowerCase() : null;
      setCurrentPlanId(planId);
    } catch (err: any) {
      console.error('Error loading pricing tiers:', err);
      setError(err?.message || 'Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleBillingCycleChange = (_event: React.MouseEvent<HTMLElement>, newCycle: 'monthly' | 'annual' | null) => {
    if (newCycle !== null) {
      setBillingCycle(newCycle);
    }
  };

  const calculateAnnualSavings = (monthlyPrice: number, annualPrice: number) => {
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - annualPrice;
    const percentage = ((savings / monthlyCost) * 100).toFixed(0);
    return { amount: savings, percentage };
  };

  const getKeyFeatures = (tier: PricingTier): string[] => {
    const features: string[] = [];
    
    features.push(`${tier.limits.scans.included} scans/month`);
    features.push(`${tier.limits.securityRules.count} security rules`);
    
    if (tier.limits.customRules.perMonth > 0) {
      features.push(`${tier.limits.customRules.perMonth} custom rules/month`);
    }
    
    if (tier.limits.plugins.count !== 0) {
      features.push(
        typeof tier.limits.plugins.count === 'number'
          ? `${tier.limits.plugins.count} plugin${tier.limits.plugins.count > 1 ? 's' : ''}`
          : 'All plugins'
      );
    }
    
    if (tier.features.dashboardAccess) features.push('Dashboard access');
    if (tier.features.apiAccess) features.push('API access');
    if (tier.features.advancedAnalytics) features.push('Advanced analytics');
    if (tier.features.detailedRemediation) features.push('Detailed remediation');
    if (tier.features.teamManagement) features.push('Team management');
    if (tier.features.whiteLabel) features.push('White-label');
    
    features.push(`${tier.support.type} support`);
    features.push(`${tier.support.responseTime} response`);
    
    return features;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* First Month Free Banner */}
      <Box
        sx={{
          bgcolor: '#FF7518',
          borderRadius: 2,
          px: 3,
          py: 1.5,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>
          🎉 First month free on any paid plan — no credit card required to get started.
        </Typography>
      </Box>

      <Box textAlign="center" mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Choose Your Plan
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Healthcare compliance scanning made simple. All plans include unlimited users.
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            aria-label="billing cycle"
          >
            <ToggleButton value="monthly" aria-label="monthly billing">
              Monthly
            </ToggleButton>
            <ToggleButton value="annual" aria-label="annual billing">
              Annual
            </ToggleButton>
          </ToggleButtonGroup>
          {billingCycle === 'annual' && (
            <Chip label="Save 16.7% (2 months free!)" color="success" size="small" />
          )}
        </Stack>
      </Box>

      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment successful! Your subscription is now active.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {tiers.map((tier) => {
          const price = billingCycle === 'annual' ? tier.pricing.annual : tier.pricing.monthly;
          const monthlyPrice = billingCycle === 'annual' ? tier.pricing.annual / 12 : tier.pricing.monthly;
          const savings =
            billingCycle === 'annual' && tier.pricing.monthly > 0
              ? calculateAnnualSavings(tier.pricing.monthly, tier.pricing.annual)
              : null;
          const isSelected = preSelectedPlan === tier.id;
          const isCurrent = !!currentPlanId && String(tier.id).toLowerCase() === currentPlanId;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tier.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isCurrent ? 3 : (tier.popular ? 3 : 1),
                  borderColor: isCurrent ? 'warning.main' : (tier.popular ? 'primary.main' : 'divider'),
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 0.2s',
                }}
              >
                {isCurrent && (
                  <Chip
                    label="Current"
                    color="warning"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 'bold',
                    }}
                  />
                )}
                {tier.popular && (
                  <Chip
                    icon={<StarIcon />}
                    label="Most Popular"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1, pt: tier.popular ? 5 : 2 }}>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {tier.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {tier.description}
                  </Typography>

                  <Box mb={2}>
                    <Typography variant="h3" component="span" fontWeight="bold">
                      ${monthlyPrice.toFixed(0)}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.secondary">
                      /month
                    </Typography>
                    
                    {billingCycle === 'annual' && price > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        ${price.toFixed(0)} billed annually
                      </Typography>
                    )}
                    
                    {savings && (
                      <Chip
                        label={`Save $${savings.amount} (${savings.percentage}%)`}
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>

                  {tier.limits.scans.overageAllowed && tier.limits.scans.overageRate > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      Overages: ${tier.limits.scans.overageRate}/scan
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <List dense disablePadding>
                    {getKeyFeatures(tier).map((feature, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {tier.restrictions && !tier.restrictions.commercialUse && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="caption">{tier.restrictions.description}</Typography>
                    </Alert>
                  )}
                </CardContent>

                <Box p={2}>
                  {isCurrent ? (
                    <Button variant="outlined" color="primary" fullWidth size="large" disabled>
                      Current Plan
                    </Button>
                  ) : tier.id !== 'free' && (
                    <RazorpayCheckout
                      tierId={tier.id}
                      tierName={tier.name}
                      billingCycle={billingCycle}
                      onSuccess={loadPricingTiers}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          All prices in USD. Cancel anytime. Questions? Contact{' '}
          <a href="mailto:sales@baastream.com">sales@baastream.com</a>
        </Typography>
      </Box>
    </Box>
  );
}

